// Judiciary: tech fouls over the last N weeks by competition, team and
// player (from Game page reports), and suspensions. Suspended players show a
// red Suspension button on their games in the Game page.

import React, { useCallback, useEffect, useState } from 'react';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

function todayStr() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function fmt(dateStr) {
  if (!dateStr) return '';
  return new Date(`${String(dateStr).slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

async function api(path, opts = {}) {
  const res = await auditFetch(`${API_BASE}${path}`, {
    ...opts,
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

export default function Judiciary() {
  const { canEdit } = usePermissions();
  const editable = canEdit('judiciary', 'judiciary');
  const [weeks, setWeeks] = useState(3);
  const [summary, setSummary] = useState(null);
  const [suspensions, setSuspensions] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(null); // suspension being raised / edited
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadSummary = useCallback(async (w) => {
    try { setSummary(await api(`/judiciary/summary?weeks=${w}`)); } catch (e) { setError(e.message); }
  }, []);
  const loadSuspensions = useCallback(async (all) => {
    try { setSuspensions(await api(`/judiciary/suspensions${all ? '?status=all' : ''}`)); } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { loadSummary(weeks); }, [weeks, loadSummary]);
  useEffect(() => { loadSuspensions(showAll); }, [showAll, loadSuspensions]);

  const refresh = () => { loadSummary(weeks); loadSuspensions(showAll); };

  async function toggleCommunicated(s) {
    try {
      await api(`/judiciary/suspensions/${s.id}`, { method: 'PUT', body: JSON.stringify({ communicated: !s.communicated }) });
      loadSuspensions(showAll);
    } catch (e) { setError(e.message); }
  }

  async function cancelSuspension(s) {
    if (!window.confirm(`Cancel ${s.playerName}'s suspension? It will no longer show on the Game page.`)) return;
    try {
      await api(`/judiciary/suspensions/${s.id}`, { method: 'DELETE' });
      setMessage(`Cancelled ${s.playerName}'s suspension.`);
      refresh();
    } catch (e) { setError(e.message); }
  }

  const totalPlayers = summary ? summary.competitions.reduce((n, c) => n + c.teams.reduce((m, t) => m + t.players.length, 0), 0) : 0;

  return (
    <div className="admin-section" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0 }}>Judiciary</h2>
          <p style={{ color: '#555', margin: '4px 0 0' }}>Tech fouls from game reports, and player suspensions.</p>
        </div>
        {editable && (
          <button className="btn-submit" style={{ marginTop: 0 }} onClick={() => setEditing({ isNew: true })}>+ Raise suspension</button>
        )}
      </div>

      {error && <div className="error-message" onClick={() => setError('')} style={{ cursor: 'pointer' }}>{error} ✕</div>}
      {message && <div className="success-message" onClick={() => setMessage('')} style={{ cursor: 'pointer' }}>{message} ✕</div>}

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <strong>Tech fouls in the last</strong>
          <input
            type="number" min={1} max={52} value={weeks}
            onChange={(e) => setWeeks(Math.min(52, Math.max(1, parseInt(e.target.value, 10) || 1)))}
            style={{ width: 64, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <strong>weeks</strong>
          {summary && <span style={{ color: '#666', fontSize: '0.85rem' }}>({fmt(summary.from)} – {fmt(summary.to)} · {totalPlayers} player{totalPlayers === 1 ? '' : 's'})</span>}
        </div>
        {!summary ? <p style={{ color: '#888' }}>Loading…</p> : summary.competitions.length === 0 ? (
          <p style={{ color: '#888', margin: 0 }}>No tech fouls recorded in game reports for this period.</p>
        ) : summary.competitions.map((c) => (
          <div key={c.name} style={{ marginBottom: 18 }}>
            <h3 style={{ margin: '8px 0 6px', color: '#A4161A' }}>{c.name}</h3>
            {c.teams.map((t) => (
              <div key={t.name} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700, margin: '4px 0' }}>{t.name}</div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Tech fouls</th>
                        <th>Ejections</th>
                        <th>Severity</th>
                        <th>Games</th>
                        <th>Suspension</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.players.map((p) => (
                        <tr key={`${p.participantId || p.name}`}>
                          <td style={{ fontWeight: 600 }}>{p.name}</td>
                          <td style={{ fontWeight: 700 }}>{p.fouls}</td>
                          <td style={p.ejections ? { color: '#c62828', fontWeight: 700 } : undefined}>{p.ejections || '–'}</td>
                          <td>
                            {p.high > 0 && <span style={{ ...pill, background: '#fdecec', color: '#c62828' }}>{p.high} High</span>}
                            {p.low > 0 && <span style={{ ...pill, background: '#f1f3f5', color: '#555' }}>{p.low} Low</span>}
                          </td>
                          <td style={{ fontSize: '0.8rem', color: '#444' }}>
                            {p.incidents.map((i, idx) => (
                              <div key={idx} title={i.comments || ''}>
                                {fmt(i.date)} v {i.opponent} · {i.count} tech{i.count === 2 ? 's' : ''} · {i.severity}{i.ejection ? ' · EJECTED' : ''}
                              </div>
                            ))}
                          </td>
                          <td>
                            {p.suspension ? (
                              <span style={{ ...pill, background: '#c62828', color: 'white' }}>{fmt(p.suspension.startDate)} – {fmt(p.suspension.endDate)}</span>
                            ) : editable ? (
                              <button
                                className="btn-delete"
                                style={{ padding: '2px 10px', fontSize: '0.75rem' }}
                                onClick={() => setEditing({ isNew: true, participantId: p.participantId, playerName: p.name, teamName: t.name === 'Team not recorded' ? '' : t.name, competition: c.name })}
                              >
                                Suspend
                              </button>
                            ) : '–'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Suspensions</h3>
          <label style={{ fontSize: '0.85rem', color: '#555', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show finished and cancelled
          </label>
        </div>
        {suspensions.length === 0 ? <p style={{ color: '#888', margin: 0 }}>{showAll ? 'No suspensions yet.' : 'No current or upcoming suspensions.'}</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Team</th>
                  <th>Weeks</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Communicated</th>
                  <th>IBA contact</th>
                  <th>Comment</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {suspensions.map((s) => {
                  const finished = s.endDate < todayStr();
                  return (
                    <tr key={s.id} style={s.cancelledAt || finished ? { color: '#999' } : undefined}>
                      <td style={{ fontWeight: 600 }}>{s.playerName}{s.cancelledAt ? ' (cancelled)' : finished ? ' (finished)' : ''}</td>
                      <td>{s.teamName || '–'}{s.competition ? <div style={{ fontSize: '0.75rem', color: '#888' }}>{s.competition}</div> : null}</td>
                      <td>{s.weeks}</td>
                      <td>{fmt(s.startDate)}</td>
                      <td>{fmt(s.endDate)}</td>
                      <td>
                        <input type="checkbox" checked={s.communicated} disabled={!editable || !!s.cancelledAt} onChange={() => toggleCommunicated(s)} />
                      </td>
                      <td>{s.ibaContactName || '–'}</td>
                      <td style={{ maxWidth: 260 }}>{s.comment || '–'}</td>
                      {editable && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {!s.cancelledAt && <button className="btn-edit" onClick={() => setEditing(s)}>Edit</button>}
                          {!s.cancelledAt && <button className="btn-delete" style={{ marginLeft: 6 }} onClick={() => cancelSuspension(s)}>Cancel</button>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <SuspensionModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); setMessage(msg); refresh(); }}
        />
      )}
    </div>
  );
}

function SuspensionModal({ initial, onClose, onSaved }) {
  const isNew = !!initial.isNew;
  const [player, setPlayer] = useState(initial.playerName ? { participantId: initial.participantId || null, name: initial.playerName } : null);
  const [teamName, setTeamName] = useState(initial.teamName || '');
  const [competition, setCompetition] = useState(initial.competition || '');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [weeks, setWeeks] = useState(initial.weeks || 1);
  const [startDate, setStartDate] = useState(initial.startDate || todayStr());
  const [communicated, setCommunicated] = useState(!!initial.communicated);
  const [ibaContactName, setIbaContactName] = useState(initial.ibaContactName || '');
  const [comment, setComment] = useState(initial.comment || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (player || query.trim().length < 2) { setMatches([]); return undefined; }
    const timer = setTimeout(() => {
      api(`/judiciary/players?q=${encodeURIComponent(query.trim())}`).then(setMatches).catch(() => setMatches([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, player]);

  const endDate = weeks >= 1 ? addDays(startDate, weeks * 7 - 1) : '';

  async function save() {
    if (!player) { setError('Choose a player.'); return; }
    setSaving(true);
    setError('');
    try {
      const body = { participantId: player.participantId, playerName: player.name, teamName, competition, weeks, startDate, communicated, ibaContactName, comment };
      if (isNew) await api('/judiciary/suspensions', { method: 'POST', body: JSON.stringify(body) });
      else await api(`/judiciary/suspensions/${initial.id}`, { method: 'PUT', body: JSON.stringify(body) });
      onSaved(`${isNew ? 'Suspension raised' : 'Suspension updated'} for ${player.name}: ${fmt(startDate)} to ${fmt(endDate)}.`);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !saving && onClose()}>
      <div style={{ background: 'white', borderRadius: 10, padding: '1.25rem 1.5rem', width: '95%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{isNew ? 'Raise suspension' : 'Edit suspension'}</h3>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>

        <label style={lbl}>Player</label>
        {player ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <strong>{player.name}</strong>
            {isNew && <button type="button" className="btn-cancel" style={{ padding: '2px 10px' }} onClick={() => { setPlayer(null); setQuery(''); }}>Change</button>}
          </div>
        ) : (
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Start typing the player's name…" style={inp} autoFocus />
            {matches.length > 0 && (
              <div style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, background: 'white', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto' }}>
                {matches.map((m) => (
                  <button
                    key={m.participantId}
                    type="button"
                    onClick={() => { setPlayer({ participantId: m.participantId, name: m.name }); setTeamName(m.teamName || ''); setCompetition(m.competition || ''); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderBottom: '1px solid #f0f0f0', background: 'white', cursor: 'pointer' }}
                  >
                    <strong>{m.name}</strong>{m.teamName ? <span style={{ color: '#666' }}> · {m.teamName}</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <label style={lbl}>Team</label>
        <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team the player is suspended from" style={{ ...inp, marginBottom: 10 }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={lbl}>Weeks</label>
            <input type="number" min={1} max={104} value={weeks} onChange={(e) => setWeeks(Math.max(1, parseInt(e.target.value, 10) || 1))} style={inp} />
          </div>
          <div>
            <label style={lbl}>Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value || todayStr())} style={inp} />
          </div>
          <div>
            <label style={lbl}>End date</label>
            <div style={{ ...inp, background: '#f1f3f5' }}>{fmt(endDate)}</div>
          </div>
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px' }}>
          <input type="checkbox" checked={communicated} onChange={(e) => setCommunicated(e.target.checked)} />
          Communicated to the player
        </label>

        <label style={lbl}>IBA name (who contacted them)</label>
        <input value={ibaContactName} onChange={(e) => setIbaContactName(e.target.value)} style={{ ...inp, marginBottom: 10 }} />

        <label style={lbl}>Comment</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} style={{ ...inp, fontFamily: 'inherit', marginBottom: 10 }} />

        {error && <p style={{ color: '#c62828', margin: '0 0 10px' }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-submit" style={{ marginTop: 0 }} onClick={save} disabled={saving || !player}>
            {saving ? 'Saving…' : isNew ? 'Raise suspension' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const card = { background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1rem 1.25rem', marginTop: '1rem' };
const pill = { display: 'inline-block', borderRadius: 999, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700, marginRight: 4 };
const lbl = { display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 };
const inp = { width: '100%', padding: '0.45rem 0.55rem', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', fontSize: '0.9rem' };
